import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import {
  ECellValueDisplayMode,
  ECellValueType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { useMemo } from 'react';
import ExcelTable from 'components/ui/excel-table';
import { priceRatingApi } from 'services/apis';
import {
  FirePricingSummaryResponseDto,
  SubmissionResponseDto,
} from 'libs/pinnacle-sdk';
import { useQuery } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';

const { Title } = Typography;

const FirePricingSummary: React.FC<{
  submission: SubmissionResponseDto;
  currency: string;
}> = ({ submission, currency }) => {
  const { t } = useTranslation();

  const { data: firePricingSummary, isLoading: isFirePricingSummaryLoading } =
    useQuery<FirePricingSummaryResponseDto[]>(
      [EQueryKeys.FIRE_PRICING_SUMMARY, submission.submissionId, currency],
      () =>
        priceRatingApi.getFirePricingSummary(submission.submissionId, currency)
    );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'layerNumber',
        headerName: t('firePricingSummary.layer'),
        width: 150,
        editable: false,
        cellRenderer: (params: { value: number }) =>
          `${t('firePricingSummary.layer')} ${params.value}`,
      },
      {
        field: 'limit',
        headerName: t('firePricingSummary.limit'),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        precision: 0,
      },
      {
        field: 'excess',
        headerName: t('firePricingSummary.excess'),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        precision: 0,
      },
      {
        field: 'fireExpectedLossCost',
        headerName: t('firePricingSummary.fireExpectedLossCost'),
        flex: 1,
        minWidth: 200,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        precision: 0,
      },
      {
        field: 'fireRateOnLine',
        headerName: t('firePricingSummary.fireRateOnLine'),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
      },
      {
        field: 'fireRateAdequacy',
        headerName: t('firePricingSummary.fireRateAdequacy'),
        flex: 1,
        minWidth: 150,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
      },
    ],
    [t]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Title level={4} className="mb-6 ml-4">
        {t('firePricingSummary.firePricingSummary')}
      </Title>

      <ExcelTable
        data={firePricingSummary || []}
        columnDefs={columnDefs}
        pagination={false}
        rowKeyColName="layerNumber"
        baseTableHeight={490}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={false}
        includeAddRow={false}
        includeDeleteCol={false}
        loading={isFirePricingSummaryLoading}
      />
    </div>
  );
};

export default FirePricingSummary;
